import React, { useState } from 'react';

interface VoiceControlsProps {
  isListening: boolean;
  onStartListening: () => void;
  onStopListening: () => void;
  disabled?: boolean;
  language: string;
}

const VoiceControls: React.FC<VoiceControlsProps> = ({
  isListening,
  onStartListening,
  onStopListening,
  disabled = false,
  language
}) => {
  const [permissionDenied, setPermissionDenied] = useState(false);

  const handleMicrophoneClick = async () => {
    if (disabled) return;
    
    if (isListening) {
      onStopListening();
    } else {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        setPermissionDenied(false);
        onStartListening();
      } catch (error) {
        console.error('Microphone permission denied:', error);
        setPermissionDenied(true);
        setTimeout(() => setPermissionDenied(false), 3000);
      }
    }
  };

  const voiceTooltips: Record<string, { denied: string; stop: string; start: string }> = {
    en: { denied: 'Please allow microphone access', stop: 'Click to stop recording', start: 'Click to start voice input' },
    'zh-TW': { denied: '請允許麥克風權限', stop: '點擊停止錄音', start: '點擊開始語音輸入' },
    'zh-CN': { denied: '请允许麦克风权限', stop: '点击停止录音', start: '点击开始语音输入' },
    da: { denied: 'Tillad venligst mikrofonadgang', stop: 'Klik for at stoppe optagelse', start: 'Klik for at starte stemmeinput' },
    de: { denied: 'Bitte Mikrofonzugriff erlauben', stop: 'Klicken zum Stoppen der Aufnahme', start: 'Klicken zum Starten der Spracheingabe' },
    es: { denied: 'Permite el acceso al micrófono', stop: 'Haz clic para detener la grabación', start: 'Haz clic para iniciar la entrada de voz' },
    fr: { denied: "Veuillez autoriser l'accès au microphone", stop: "Cliquez pour arrêter l'enregistrement", start: 'Cliquez pour commencer la saisie vocale' },
    it: { denied: "Consenti l'accesso al microfono", stop: 'Clicca per interrompere la registrazione', start: 'Clicca per avviare input vocale' },
    nl: { denied: 'Sta microfoon toegang toe', stop: 'Klik om opname te stoppen', start: 'Klik om spraakinvoer te starten' },
    pt: { denied: 'Permita o acesso ao microfone', stop: 'Clique para parar a gravação', start: 'Clique para iniciar entrada de voz' },
    ja: { denied: 'マイクへのアクセスを許可してください', stop: 'クリックで録音停止', start: 'クリックで音声入力開始' },
    ko: { denied: '마이크 접근을 허용해 주세요', stop: '클릭하여 녹음 중지', start: '클릭하여 음성 입력 시작' },
    th: { denied: 'กรุณาอนุญาตการเข้าถึงไมโครโฟน', stop: 'คลิกเพื่อหยุดบันทึก', start: 'คลิกเพื่อเริ่มป้อนเสียง' }
  };

  const getMicrophoneTooltip = () => {
    const tips = voiceTooltips[language] || voiceTooltips.en;
    if (permissionDenied) return tips.denied;
    if (isListening) return tips.stop;
    return tips.start;
  };


  return (
    <div className="voice-controls" style={{
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
      marginRight: '8px'
    }}>
      <button
        type="button"
        className={`voice-control-btn microphone-btn ${isListening ? 'listening' : ''} ${permissionDenied ? 'error' : ''}`}
        onClick={handleMicrophoneClick}
        disabled={disabled}
        title={getMicrophoneTooltip()}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          border: isListening ? '2px solid #ff4444' : '1px solid #ddd',
          backgroundColor: isListening ? '#ffeeee' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          position: 'relative',
          animation: isListening ? 'pulse 1.5s infinite' : 'none'
        }}
      >
        <i 
          className={`fas ${isListening ? 'fa-microphone-slash' : 'fa-microphone'}`}
          style={{
            color: permissionDenied ? '#ff4444' : (isListening ? '#ff4444' : '#666'),
            fontSize: '16px'
          }}
        />
        {isListening && (
          <span className="recording-indicator" style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            width: '12px',
            height: '12px',
            backgroundColor: '#ff4444',
            borderRadius: '50%',
            animation: 'blink 1s infinite'
          }} />
        )}
      </button>

      <style jsx>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(255, 68, 68, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(255, 68, 68, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(255, 68, 68, 0);
          }
        }

        @keyframes blink {
          0%, 50%, 100% {
            opacity: 1;
          }
          25%, 75% {
            opacity: 0.5;
          }
        }

        .voice-control-btn:hover:not(:disabled) {
          transform: scale(1.1);
        }

        .voice-control-btn:active:not(:disabled) {
          transform: scale(0.95);
        }

        .voice-control-btn:disabled {
          opacity: 0.5;
        }
      `}</style>
    </div>
  );
};

export default VoiceControls;